import { useTranslation } from 'next-i18next';
import { memo } from 'react';
import { IAchievement } from '@bridebook/models/source/models/Suppliers/Achievements.types';
import { IBadge } from '@bridebook/models/source/models/Suppliers/Badges.types';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { Accordion, Box } from '@bridebook/ui';
import AwardHeader from '@bridebook/web/app-basic/components/supplier/sections/awards/awards-header';
import Badges from '@bridebook/web/app-shared/components/supplier/sections/badges/badges';
import { AWARDS_SECTION_ID } from '@bridebook/web/app-shared/components/supplier/sections/ids';
import AwardCategory from '@bridebook/web/app-shared/components/supplier/sections/awards/award-category';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/awards/awards.style';
import { AwardData, IBadgeWithYear } from '@bridebook/web/app-shared/lib/supplier/types';

interface IProps {
  achievements?: IAchievement[];
  badges?: Omit<IBadge, 'createdAt' | 'updatedAt'>[];
  supplierCountry?: CountryCodes;
  onToggleAccordion?: (isOpen: boolean) => void;
}

const Awards = ({ achievements = [], badges = [], onToggleAccordion, supplierCountry }: IProps) => {
  const { t } = useTranslation('supplier');
  const badgeList = badges.filter(hasYear).sort((item) => -Number(item.period.y));

  // convert to array to abstract from object keys
  const filteredFeaturedIn: AwardData[] = achievements
    .filter((a) => a.type === 'media')
    .map((a) => [a.name, a.link]);

  const filteredIndustryAwards: AwardData[] = achievements
    .filter((a) => a.type === 'award')
    .map((a) => [a.name, a.link]);

  const filteredAccreditationsAssociations: AwardData[] = achievements
    .filter((a) => a.type === 'accreditation')
    .map((a) => [a.name, a.link]);

  const totalAwardLength =
    filteredFeaturedIn.length +
    filteredIndustryAwards.length +
    filteredAccreditationsAssociations.length +
    badgeList.length;

  if (!totalAwardLength) {
    return null;
  }

  const styles = componentStyles();

  return (
    <Box style={styles.accordionWrapper} id={AWARDS_SECTION_ID}>
      <Accordion
        noRoundBorder
        noTopBorder
        arrowStartDown
        noSidePaddings
        noBgColor
        onToggle={onToggleAccordion}
        text={
          <Box>
            <Box style={styles.awardsTitle}>{t('supplier.awards.category.press.title')}</Box>
            <Box style={styles.awardsDescription}>
              {t('supplier.awards.accordion.name.description')}
            </Box>
          </Box>
        }>
        <Box mt={4} mb={4}>
          <Box style={styles.awardListWrap}>
            {filteredFeaturedIn.length > 0 && (
              <Box style={styles.awardListItem(1)}>
                <AwardHeader
                  heading={t('supplier.awards.category.press.title')}
                  count={filteredFeaturedIn.length}
                />
                <AwardCategory awards={filteredFeaturedIn} />
              </Box>
            )}
            {filteredIndustryAwards.length > 0 && (
              <Box style={styles.awardListItem(2)}>
                <AwardHeader
                  heading={t('supplier.awards.category.industry.title')}
                  count={filteredIndustryAwards.length}
                />
                <AwardCategory awards={filteredIndustryAwards} />
              </Box>
            )}
            {filteredAccreditationsAssociations.length > 0 && (
              <Box style={styles.awardListItem(3)}>
                <AwardHeader
                  heading={t('supplier.awards.category.associaions.title')}
                  count={filteredAccreditationsAssociations.length}
                />
                <AwardCategory awards={filteredAccreditationsAssociations} />
              </Box>
            )}
            {badgeList.length > 0 && (
              <Box style={styles.awardListItem(4)}>
                <AwardHeader
                  heading={t('supplier.awards.category.badges.title')}
                  count={badgeList.length}
                />
                <Badges badges={badgeList} supplierCountry={supplierCountry} />
              </Box>
            )}
          </Box>
        </Box>
      </Accordion>
    </Box>
  );
};

export default memo(Awards);

const hasYear = (badge: any): badge is IBadgeWithYear => !!badge.period?.y;
